import {
	createContext,
	useState,
	ReactNode,
	Dispatch,
	SetStateAction,
	useRef,
	useCallback,
} from 'react';

export type ModalMobileSheet = 'default' | 'tall';

interface OpenModalProps {
	content: ReactNode;
	title?: ReactNode | null;
	size?: 'default' | 'wide' | 'xlarge' | 'compact';
	/** Quando false, o botão de confirmar não fecha o modal (ex.: revisão → pagamento no mesmo drawer). */
	closeOnConfirm?: boolean;
	/** Drawer mobile mais alto (checkout de consulta). Não afeta desktop nem modais compactos. */
	mobileSheet?: ModalMobileSheet;
	/** Texto do botão de confirmar. Alternativa a chamar setConfirmText. */
	confirmText?: string;
	/** Texto do botão de cancelar. Alternativa a chamar setCancelText. */
	cancelText?: string;
	/** Ação do botão de confirmar. Alternativa a chamar setOnConfirm. */
	onConfirm?: () => void;
}

type ModalSnapshot = {
	content: ReactNode;
	title: ReactNode;
	size: 'default' | 'wide' | 'xlarge' | 'compact';
	mobileSheet: ModalMobileSheet;
	closeOnConfirm: boolean;
	cancelText: string;
	confirmText: string;
	isDisabledConfirm: boolean;
	onConfirm: (() => void) | null;
};

type ModalContextType = {
	isOpen: boolean;
	content: ReactNode;
	title: ReactNode;
	size: 'default' | 'wide' | 'xlarge' | 'compact';
	mobileSheet: ModalMobileSheet;
	openModal: (props: OpenModalProps) => void;
	/** Fecha o modal atual ou volta um nível na pilha (ex.: benefício → pagamento). */
	closeModal: () => void;
	/** Fecha todos os níveis (ex.: após pagamento ou cancelar checkout). */
	closeAllModals: () => void;
	onConfirm?: () => void;
	setOnConfirm: (fn: () => void) => void;
	cancelText?: string;
	setCancelText: Dispatch<SetStateAction<string>>;
	confirmText?: string;
	setConfirmText: Dispatch<SetStateAction<string>>;
	isDisabledConfirm: boolean;
	setIsDisabledConfirm: Dispatch<SetStateAction<boolean>>;
};

export const ModalContext = createContext<ModalContextType | undefined>(
	undefined,
);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode>(null);
	const [title, setTitle] = useState<ReactNode>(null);
	const [size, setSize] = useState<'default' | 'wide' | 'xlarge' | 'compact'>(
		'default',
	);
	const [mobileSheet, setMobileSheet] = useState<ModalMobileSheet>('default');
	const [cancelText, setCancelText] = useState<string>('');
	const [confirmText, setConfirmText] = useState<string>('');
	const [isDisabledConfirm, setIsDisabledConfirm] = useState<boolean>(false);

	const onConfirmRef = useRef<(() => void) | null>(null);
	const closeOnConfirmRef = useRef(true);
	const modalStackRef = useRef<ModalSnapshot[]>([]);

	const setOnConfirm = useCallback((callback: () => void) => {
		onConfirmRef.current = callback;
	}, []);

	const restoreModalSnapshot = useCallback((snapshot: ModalSnapshot) => {
		closeOnConfirmRef.current = snapshot.closeOnConfirm;
		onConfirmRef.current = snapshot.onConfirm;
		setContent(snapshot.content);
		setTitle(snapshot.title);
		setSize(snapshot.size);
		setMobileSheet(snapshot.mobileSheet);
		setCancelText(snapshot.cancelText);
		setConfirmText(snapshot.confirmText);
		setIsDisabledConfirm(snapshot.isDisabledConfirm);
		setIsOpen(true);
	}, []);

	const resetModal = useCallback(() => {
		modalStackRef.current = [];
		setIsOpen(false);
		setContent(null);
		setTitle(null);
		setSize('default');
		setMobileSheet('default');
		onConfirmRef.current = null;
		closeOnConfirmRef.current = true;
		setCancelText('');
		setConfirmText('');
		setIsDisabledConfirm(false);
	}, []);

	const openModal = ({
		content: nextContent,
		title: nextTitle,
		size: modalSize,
		closeOnConfirm = true,
		mobileSheet: sheet = 'default',
		confirmText: nextConfirmText,
		cancelText: nextCancelText,
		onConfirm: nextOnConfirm,
	}: OpenModalProps) => {
		if (isOpen) {
			modalStackRef.current.push({
				content,
				title,
				size,
				mobileSheet,
				closeOnConfirm: closeOnConfirmRef.current,
				cancelText,
				confirmText,
				isDisabledConfirm,
				onConfirm: onConfirmRef.current,
			});
			// Modal empilhado começa limpo, para não herdar botões nem a ação
			// do modal de baixo. Para empilhados, passe os textos/ação por props
			// ou chame os setters DEPOIS de openModal.
			onConfirmRef.current = null;
			setCancelText('');
			setConfirmText('');
			setIsDisabledConfirm(false);
		}
		// Modal novo (nada aberto): o estado já foi zerado no último fechamento
		// (resetModal). Não limpar aqui preserva textos/ação definidos pelo
		// chamador logo ANTES de openModal, que é o padrão da maioria das telas
		// (ex.: cancelar assinatura, banir usuário, excluir post).

		if (nextConfirmText !== undefined) setConfirmText(nextConfirmText);
		if (nextCancelText !== undefined) setCancelText(nextCancelText);
		if (nextOnConfirm !== undefined) onConfirmRef.current = nextOnConfirm;
		closeOnConfirmRef.current = closeOnConfirm;
		setContent(nextContent);
		setTitle(nextTitle ?? null);
		setSize(modalSize ?? 'default');
		setMobileSheet(sheet);
		setIsOpen(true);
	};

	const closeModal = useCallback(() => {
		const previous = modalStackRef.current.pop();
		if (previous) {
			restoreModalSnapshot(previous);
			return;
		}
		resetModal();
	}, [resetModal, restoreModalSnapshot]);

	const closeAllModals = useCallback(() => {
		resetModal();
	}, [resetModal]);

	const handleConfirm = useCallback(() => {
		onConfirmRef.current?.();
		if (closeOnConfirmRef.current) {
			closeModal();
		}
	}, [closeModal]);

	return (
		<ModalContext.Provider
			value={{
				isOpen,
				content,
				title,
				size,
				mobileSheet,
				openModal,
				closeModal,
				closeAllModals,
				onConfirm: handleConfirm,
				setOnConfirm,
				cancelText,
				setCancelText,
				confirmText,
				setConfirmText,
				isDisabledConfirm,
				setIsDisabledConfirm,
			}}
		>
			{children}
		</ModalContext.Provider>
	);
};
